import { Box, Divider, FormHelperText, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import { SEX_LIST } from '@/common/constants/common';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppEstimatedAgeTextField from '@/common/components/form-inputs/AppEstimatedAgeTextField';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import SingleCheckbox from '@/common/components/form-inputs/SingleCheckbox';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';
import { useGetStaffAllListQuery } from '@/api/common';
import { eightDigitPhone } from '@/utils/validationHelper';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditReferralForICCMWForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const userInfo = useSelector(selectMyInfo);

		const defaultValues: DefaultValues<any> = {
			sex: null,
		};

		const { data: staffList }: any = useGetStaffAllListQuery(
			userInfo?.currentBranch?.id || '',
			{
				refetchOnMountOrArgChange: true,
				skip: !userInfo?.currentBranch,
			}
		);

		useEffect(() => {
			if (details) methods.reset(details);
		}, [details]);

		const formSchema = Yup.object().shape({
			referralDate: Yup.string()
				.nullable()
				.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
			contactTel: Yup.string()
				.nullable()
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
			othersSpecify: Yup.string().when('others', {
				is: (is: boolean) => is,
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
			therapyValidation: Yup.string().test(
				'at-least-one-therapy',
				t('errorMessage.pleaseSelect'), // 错误提示
				function () {
					const {
						receivingTherapy,
						occupationalTherapy,
						outreaching,
						trainingServices,
					} = this.parent;

					if (receivingTherapy === true) {
						const hasSelected =
							occupationalTherapy === true ||
							outreaching === true ||
							trainingServices === true;

						return hasSelected;
					}

					// 如果 receivingTherapy 不为 true，跳过验证
					return true;
				}
			),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		const watchrReceivingTherapy = methods.watch('receivingTherapy');

		const watchOthersSpecify = methods.watch('others');

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.receiptOfReferralForICCMW')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='nameEn'
									label={t('preAdmission.name')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='hkicNo'
									label={t('preAdmission.hkicNo')}
								/>
							</Grid>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.sex')}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='sex'
									row
									options={SEX_LIST}
								/>
							</Grid>
							<Grid size={6}>
								<AppEstimatedAgeTextField
									control={methods.control}
									label={t('preAdmission.age')}
								/>
							</Grid>
							<Grid size={6}>
								<AppDatePicker
									control={methods.control}
									name='referralDate'
									label={t(
										'preAdmission.referringAboveNamedServiceUserUouReferredOn'
									)}
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
								/>
							</Grid>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.serviceRendered')}
								</Typography>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='counsellingToServiceUser'
										label={t('preAdmission.mentalSuspectedMentalProblems')}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='counsellingToFamily'
										label={t('preAdmission.memberCarerServiceUser')}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='successfulEngagement'
										label={t(
											'preAdmission.supportDevelopmentalGroupsEducationalProgrammes'
										)}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='receivingTherapy'
										label={t(
											'preAdmission.therapyOutreachingTrainingServicesOfOurICCMW'
										)}
										handleChange={() => {
											methods.trigger('therapyValidation');
										}}
									/>
								</Box>
								<Box display={'flex'} marginLeft={'32px'}>
									<SingleCheckbox
										control={methods.control}
										name='occupationalTherapy'
										label={t('preAdmission.occupationalTherapy')}
										handleChange={() => {
											methods.trigger('therapyValidation');
										}}
										disabled={!watchrReceivingTherapy}
									/>
								</Box>
								<Box display={'flex'} marginLeft={'32px'}>
									<SingleCheckbox
										control={methods.control}
										name='outreaching'
										label={t('preAdmission.outreaching')}
										handleChange={() => {
											methods.trigger('therapyValidation');
										}}
										disabled={!watchrReceivingTherapy}
									/>
								</Box>
								<Box display={'flex'} marginLeft={'32px'}>
									<SingleCheckbox
										control={methods.control}
										name='trainingServices'
										label={t('preAdmission.trainingServices')}
										handleChange={() => {
											methods.trigger('therapyValidation');
										}}
										disabled={!watchrReceivingTherapy}
									/>
								</Box>
								<FormHelperText color='error' style={{ color: '#d32f2f' }}>
									{methods.formState.errors?.therapyValidation
										? t('errorMessage.pleaseSelect')
										: ''}
								</FormHelperText>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='successfullyReferred'
										label={t('preAdmission.successfullyReferred')}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='peerSupportService'
										label={t('preAdmission.peerSupportService')}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='clinicalPsychologicalService'
										label={t('preAdmission.clinicalPsychologicalService')}
									/>
								</Box>
								<Box display={'flex'}>
									<SingleCheckbox
										sx={{
											flexShrink: 0,
											'& .MuiFormControlLabel-label': {
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
											},
										}}
										control={methods.control}
										name='others'
										label={t('preAdmission.othersSpecify')}
										handleChange={() => {
											methods.setValue('othersSpecify', '');
											methods.clearErrors('othersSpecify');
										}}
									/>
									<AppTextField
										disabled={!watchOthersSpecify}
										variantType={'standard'}
										control={methods.control}
										name='othersSpecify'
										sx={{ position: 'relative', top: '-10px' }}
										label={`${t('preAdmission.specify')}${
											watchOthersSpecify ? '*' : ''
										}`}
									/>
								</Box>
							</Grid>
							<Grid size={12}>
								<AppTextField
									control={methods.control}
									multiline
									name='remarks'
									label={t('preAdmission.remarks')}
								/>
							</Grid>
						</Grid>
						<Divider sx={{ margin: '0 0 24px' }} />
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.shouldEnquiries')}
								</Typography>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='contactWorker'
									label={t('preAdmission.name')}
									options={staffList?.data || []}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='contactTel'
									label={t('preAdmission.telNo')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='contactOfficerInCharge'
									label={t('preAdmission.officerInCharge')}
								/>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);

EditReferralForICCMWForm.displayName = 'EditReferralForICCMWForm';

export default EditReferralForICCMWForm;
